import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from '../static/styles/EditProfile.module.css';
import { getUserProfile, updateUserProfile } from '../api/profile';
import dummyAvatar from '../static/img/Sidebar/Avatar.png';
import MessagePopup from '../components/MessagePopup';

const EditProfile = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [bio, setBio] = useState("");
  const [profileImage, setProfileImage] = useState(dummyAvatar);
  const [stats, setStats] = useState({
    tournaments: 0,
    completedTasks: 0,
    credits: 0,
    redeemedItems: 0
  });

  const [loading, setLoading] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Edit Profile | Jazz ESL";
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await getUserProfile();
      if (response.success) {
        setUsername(response.username || "");
        setEmail(response.email || "");
        setGender(response.gender?.toLowerCase() || "");
        setBio(response.bio || "");
        setDateOfBirth(
          response.dateOfBirth
            ? response.dateOfBirth.split("-").reverse().join("-")
            : ""
        );
        setProfileImage(response.profileImage || dummyAvatar);
        setStats({
          tournaments: response.total_tournament || 0,
          completedTasks: response.completed_tasks_total || 0,
          credits: response.credits || 0,
          redeemedItems: response.items_redeemed || 0
        });
      }
    } catch (error) {
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert(t('sel_valid_img'));
        return;
      }
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        alert(t('file_s_lim'));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setProfileImage(reader.result); // Store Base64 string in profileImage
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setLoading(true); // Start loading
    try {
      const profileData = {
        username,
        email,
        gender,
        bio,
        dateOfBirth,
        image: profileImage // Include Base64 string in the payload
      };

      const response = await updateUserProfile(profileData);

      if (response.status) {
        setPopupMessage(t('prof_update_succ'));
        setIsPopupOpen(true);
        await fetchUserProfile();
      } else {
        setPopupMessage(`${t('fail_prof_upd')} ${response.message}`);
        setIsPopupOpen(true);
      }
    } catch (error) {
      setPopupMessage(t('err_prof_upd'));
      setIsPopupOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.editProfile}>
      <div className={styles.header}>
        <span className={styles.back} onClick={() => navigate(-1)}>‹</span>
        <h3 className={styles.title}>{t('edit_prof')}</h3>
      </div>

      <div className={styles.statsBox}>
        <div className={styles.stat}>
          <div className={styles.value}>{stats.tournaments}</div>
          <div className={styles.label}>{t('tours_played')}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.value}>{stats.completedTasks}</div>
          <div className={styles.label}>{t('compl_tasks')}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.value}>{stats.credits}</div>
          <div className={styles.label}>{t('crds_earn')}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.value}>{stats.redeemedItems}</div>
          <div className={styles.label}>{t('redeemed_itms')}</div>
        </div>
      </div>

      <div className={styles.profileImageSection}>
        <div className={styles.avatarCircle}>
          <label htmlFor="imageUpload">
            <img src={profileImage} alt="Profile" className={styles.profileImage} />
          </label>
          <input
            id="imageUpload"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
        </div>
        <label htmlFor="imageUpload" className={styles.changeImageText}>
          {t('ch_img')}
        </label>
      </div>

      <div className={styles.formGroup}>
        <label>{t('usrname')}</label>
        <input value={username} onChange={e => setUsername(e.target.value)} placeholder={t('enter_usrname')} />
      </div>
      <div className={styles.formGroup}>
        <label>{t('email')}</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={t('enter_email')}
        />
      </div>
      <div className={styles.formGroup}>
        <label>{t('gender')}</label>
        <select value={gender} onChange={e => setGender(e.target.value)}>
          <option value="">{t('sel_gender')}</option>
          <option value="male">{t('male')}</option>
          <option value="female">{t('female')}</option>
        </select>
      </div>
      <div className={styles.formGroup}>
        <label>{t('bio')}</label>
        <input value={bio} onChange={e => setBio(e.target.value)} placeholder={t('bio_msg')} />
      </div>
      <div className={styles.formGroup}>
        <label>{t('dob')}</label>
        <input
          type="date"
          value={dateOfBirth}
          onChange={e => setDateOfBirth(e.target.value)}
          placeholder={t('sel_dob')}
        />
      </div>

      <button
        className={`${styles.saveButton} ${loading ? styles.loading : ''}`}
        onClick={handleSave}
        disabled={loading}
      >
        {loading ? t('saving') : t('save_changs')}
      </button>

      <MessagePopup
        isOpen={isPopupOpen}
        message={popupMessage}
        onClose={() => setIsPopupOpen(false)}
      />
    </div>
  );
};

export default EditProfile;